import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trainingCampAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import {
  Users,
  Calendar,
  Clock,
  Video,
  FileText,
  Search,
  Plus,
  Eye,
  EyeOff,
  Trash2,
  Edit,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Filter,
  RefreshCw,
  X,
  Layers,
  CalendarDays,
  Download,
  ChevronLeft,
  ChevronRight,
  Check
} from 'lucide-react';
import './TrainingCamp.css';

const PRESET_TIME_SLOTS = [
  { start: '10:00 AM', end: '10:30 AM' },
  { start: '10:30 AM', end: '11:00 AM' },
  { start: '11:00 AM', end: '11:30 AM' },
  { start: '11:30 AM', end: '12:00 PM' },
  { start: '12:00 PM', end: '12:30 PM' },
  { start: '02:00 PM', end: '02:30 PM' },
  { start: '02:30 PM', end: '03:00 PM' },
  { start: '03:00 PM', end: '03:30 PM' },
  { start: '03:30 PM', end: '04:00 PM' },
  { start: '04:00 PM', end: '04:30 PM' },
  { start: '04:30 PM', end: '05:00 PM' },
  { start: '05:00 PM', end: '05:30 PM' },
  { start: '05:30 PM', end: '06:00 PM' },
  { start: '06:00 PM', end: '06:30 PM' },
  { start: '06:30 PM', end: '07:00 PM' },
  { start: '07:00 PM', end: '07:30 PM' },
  { start: '07:30 PM', end: '08:00 PM' },
];

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Generate 30-min time options for select dropdowns (e.g. 06:00 AM to 11:30 PM)
const TIME_SELECT_OPTIONS = [];
for (let h = 6; h <= 23; h++) {
  for (let m = 0; m < 60; m += 30) {
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    const str = `${String(hour12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
    TIME_SELECT_OPTIONS.push(str);
  }
}

// Convert "hh:mm A" string to minutes from midnight
const timeStringToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return 0;
  let [, h, m, period] = match;
  let hours = parseInt(h, 10);
  const minutes = parseInt(m, 10);
  if (period.toUpperCase() === 'PM' && hours < 12) hours += 12;
  if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
  return hours * 60 + minutes;
};

// Convert minutes from midnight back to "hh:mm A"
const minutesToTimeString = (totalMinutes) => {
  const norm = ((totalMinutes % 1440) + 1440) % 1440;
  const h = Math.floor(norm / 60);
  const m = norm % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(hour12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
};

// Generate 30-minute intervals between start and end times
const generate30MinIntervals = (startStr, endStr) => {
  const startMin = timeStringToMinutes(startStr);
  const endMin = timeStringToMinutes(endStr);
  if (endMin <= startMin) return [];

  const intervals = [];
  for (let m = startMin; m + 30 <= endMin; m += 30) {
    intervals.push({
      start: minutesToTimeString(m),
      end: minutesToTimeString(m + 30)
    });
  }
  return intervals;
};

// Format date to YYYY-MM-DD
const formatDateYMD = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format date for pill display: "19 Nov 2026"
const formatDatePill = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const TrainingCamp = ({ defaultTab }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();

  const activeTab = searchParams.get('tab') || defaultTab || 'submissions';

  // State: Common
  const [showAllCamps, setShowAllCamps] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');

  // State: Tab 1 - Submissions
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [counts, setCounts] = useState({
    total_registered: 0,
    drafts_pending: 0,
    step1_submitted: 0,
    interview_booked: 0,
  });
  const [stepFilter, setStepFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  // Candidate Review Modal
  const [reviewCandidate, setReviewCandidate] = useState(null);
  const [updateStatus, setUpdateStatus] = useState('pending');
  const [adminNotes, setAdminNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // State: Tab 2 - Slots
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slots, setSlots] = useState([]);

  // Create Slot Dialog
  const [showCreateSlotModal, setShowCreateSlotModal] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [manualDateInput, setManualDateInput] = useState('');
  const [selectedPresetTimes, setSelectedPresetTimes] = useState([]);
  const [rangeStartTime, setRangeStartTime] = useState('10:00 AM');
  const [rangeEndTime, setRangeEndTime] = useState('01:00 PM');
  const [customStartTime, setCustomStartTime] = useState('');
  const [customEndTime, setCustomEndTime] = useState('');
  const [slotCapacity, setSlotCapacity] = useState(1);
  const [slotNotes, setSlotNotes] = useState('Online Google Meet interview');
  const [creatingSlots, setCreatingSlots] = useState(false);

  // Switch Tab
  const handleTabChange = (tabName) => {
    setSearchParams({ tab: tabName });
  };

  // 1. Initial Load: Submissions
  useEffect(() => {
    setCurrentPage(1);
    loadSubmissions(1, pageSize);
  }, [selectedBatchId, stepFilter, statusFilter, showAllCamps]);

  const loadSubmissions = async (page = currentPage, limit = pageSize) => {
    try {
      setSubmissionsLoading(true);
      const params = {
        page,
        limit,
        show_all: showAllCamps,
      };
      if (selectedBatchId) params.batch_id = selectedBatchId;
      if (stepFilter) params.step_filter = stepFilter;
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;

      const res = await trainingCampAPI.listSubmissions(params);
      if (res.data?.success) {
        const subs = res.data.data?.submissions || res.data.submissions || (Array.isArray(res.data.data) ? res.data.data : []);
        const cnts = res.data.data?.counts || res.data.counts || res.data.metrics || {};
        const bts = res.data.data?.batches || res.data.batches || [];
        const pag = res.data.data?.pagination || res.data.pagination || { total: subs.length, totalPages: 1 };
        setSubmissions(subs);
        setCounts(cnts);
        setBatches(bts);
        setPagination(pag);
      }
    } catch (err) {
      console.error('Failed to load submissions:', err);
      showToast(err.response?.data?.message || 'Failed to fetch candidate submissions', 'error');
    } finally {
      setSubmissionsLoading(false);
    }
  };

  // Export filtered candidates to CSV (all records matching current filter)
  const handleDownloadCsv = async () => {
    try {
      let rowsToExport = submissions;
      if (pagination.total > submissions.length) {
        showToast('Preparing complete CSV export...', 'info');
        const params = {
          page: 1,
          limit: 10000,
          show_all: showAllCamps,
        };
        if (selectedBatchId) params.batch_id = selectedBatchId;
        if (stepFilter) params.step_filter = stepFilter;
        if (statusFilter) params.status = statusFilter;
        if (searchQuery) params.search = searchQuery;

        const res = await trainingCampAPI.listSubmissions(params);
        if (res.data?.success) {
          rowsToExport = res.data.data?.submissions || res.data.submissions || submissions;
        }
      }

      if (!rowsToExport || rowsToExport.length === 0) {
        showToast('No candidate data available to export', 'error');
        return;
      }

      const headers = [
        'Registration ID',
        'Candidate Name',
        'Mobile Number',
        'Email',
        'Gender',
        'DOB',
        'Blood Group',
        'Camp Batch Date',
        'Step 1 Status',
        'Step 1 Submitted At',
        'Video Intro Link',
        'Why Join Link',
        'Step 2 Status',
        'Interview Date',
        'Interview Time',
        'Review Status',
        'Admin Notes',
        'Registered Date'
      ];

      const csvRows = rowsToExport.map((s) => {
        const step1Str = s.is_step1_submitted || s.isStep1Submitted
          ? 'Completed'
          : (s.status === 'draft' ? 'Draft Saved' : 'Not Started');
        const step2Str = s.is_interview_booked || s.isInterviewBooked
          ? 'Booked'
          : (s.is_step1_submitted || s.isStep1Submitted ? 'Slot Pending' : 'Pending Step 1');
        const slotDate = s.slot?.date || s.interviewSlot?.date || '';
        const slotTime = s.slot
          ? `${s.slot.start_time || ''} - ${s.slot.end_time || ''}`
          : (s.interviewSlot ? `${s.interviewSlot.startTime || ''} - ${s.interviewSlot.endTime || ''}` : '');

        return [
          s.registrationId || '',
          `"${(s.name || '').replace(/"/g, '""')}"`,
          `"${s.phone || ''}"`,
          `"${(s.email || '').replace(/"/g, '""')}"`,
          s.gender || '',
          s.dob ? new Date(s.dob).toLocaleDateString('en-GB') : '',
          s.bloodGroup || s.blood_group || '',
          `"${(s.batch_date_label || s.batchDateLabel || '').replace(/"/g, '""')}"`,
          step1Str,
          s.step1_submitted_at ? new Date(s.step1_submitted_at).toLocaleString('en-IN') : '',
          `"${(s.video_intro_url || s.videoIntroUrl || '').replace(/"/g, '""')}"`,
          `"${(s.why_join_url || s.whyJoinUrl || '').replace(/"/g, '""')}"`,
          step2Str,
          slotDate,
          `"${slotTime}"`,
          s.status || 'pending',
          `"${(s.admin_notes || s.adminNotes || '').replace(/"/g, '""')}"`,
          s.registered_at ? new Date(s.registered_at).toLocaleString('en-IN') : ''
        ].join(',');
      });

      const csvContent = '\uFEFF' + [headers.join(','), ...csvRows].join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `training_camp_volunteers_${dateStr}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast('Candidates CSV exported successfully', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to export CSV spreadsheet', 'error');
    }
  };

  // 2. Load Slots
  useEffect(() => {
    if (activeTab === 'slots') {
      loadSlots();
    }
  }, [activeTab, selectedBatchId, showAllCamps]);

  const loadSlots = async () => {
    try {
      setSlotsLoading(true);
      const params = { show_all: showAllCamps };
      if (selectedBatchId) params.batch_id = selectedBatchId;

      const res = await trainingCampAPI.listSlots(params);
      if (res.data?.success) {
        setSlots(res.data.data?.slots || res.data.slots || []);
        if (res.data.data?.batches || res.data.batches) {
          setBatches(res.data.data?.batches || res.data.batches);
        }
      }
    } catch (err) {
      console.error('Failed to load slots:', err);
      showToast(err.response?.data?.message || 'Failed to load interview slots', 'error');
    } finally {
      setSlotsLoading(false);
    }
  };

  // Save Candidate Status / Notes
  const handleSaveReview = async () => {
    if (!reviewCandidate) return;
    try {
      setSavingNotes(true);
      const res = await trainingCampAPI.updateSubmission(reviewCandidate.id, {
        status: updateStatus,
        admin_notes: adminNotes,
      });
      if (res.data?.success) {
        showToast('Candidate application updated successfully', 'success');
        setReviewCandidate(null);
        loadSubmissions(currentPage, pageSize);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update candidate', 'error');
    } finally {
      setSavingNotes(false);
    }
  };

  // Toggle Slot Visibility (Hide/Show)
  const handleToggleSlotVisibility = async (slot) => {
    try {
      const currentVisible = slot.isVisible !== undefined ? slot.isVisible : (slot.is_visible !== undefined ? Boolean(slot.is_visible) : true);
      const newVisible = !currentVisible;
      const res = await trainingCampAPI.updateSlot(slot.id, {
        is_visible: newVisible,
        isVisible: newVisible,
      });
      if (res.data?.success) {
        showToast(
          `Slot ${newVisible ? 'is now visible' : 'is now hidden'} to candidates`,
          'success'
        );
        loadSlots();
      }
    } catch (err) {
      showToast('Failed to update slot visibility', 'error');
    }
  };

  // Delete Slot
  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this interview slot?')) return;
    try {
      const res = await trainingCampAPI.deleteSlot(slotId);
      if (res.data?.success) {
        showToast('Interview slot deleted successfully', 'success');
        loadSlots();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete slot', 'error');
    }
  };

  // Handle adding 30-min intervals in range
  const handleAddRangeSlots = () => {
    if (!rangeStartTime || !rangeEndTime) {
      showToast('Please select both start and end time', 'error');
      return;
    }
    const intervals = generate30MinIntervals(rangeStartTime, rangeEndTime);
    if (intervals.length === 0) {
      showToast('End time must be at least 30 minutes after start time', 'error');
      return;
    }

    let addedCount = 0;
    setSelectedPresetTimes((prev) => {
      const existingKeys = new Set(prev.map((s) => `${s.start}-${s.end}`));
      const toAdd = intervals.filter((s) => !existingKeys.has(`${s.start}-${s.end}`));
      addedCount = toAdd.length;
      return [...prev, ...toAdd];
    });

    if (addedCount > 0) {
      showToast(`Added ${addedCount} 30-minute interval slot(s)!`, 'success');
    } else {
      showToast('All slots in this range are already added', 'info');
    }
  };

  const removeSelectedTimeSlot = (slotToRemove) => {
    setSelectedPresetTimes((prev) =>
      prev.filter((s) => !(s.start === slotToRemove.start && s.end === slotToRemove.end))
    );
  };

  const clearAllSelectedTimeSlots = () => {
    setSelectedPresetTimes([]);
  };

  // Handle Preset Time Selection
  const togglePresetTime = (slot) => {
    const key = `${slot.start}-${slot.end}`;
    if (selectedPresetTimes.some((s) => `${s.start}-${s.end}` === key)) {
      setSelectedPresetTimes((prev) => prev.filter((s) => `${s.start}-${s.end}` !== key));
    } else {
      setSelectedPresetTimes((prev) => [...prev, slot]);
    }
  };

  // Toggle Date Selection for Calendar
  const toggleDateSelection = (dateStr) => {
    setSelectedDates((prev) =>
      prev.includes(dateStr)
        ? prev.filter((d) => d !== dateStr)
        : [...prev, dateStr].sort()
    );
  };

  // Add Manual Date Input
  const handleAddManualDate = () => {
    if (!manualDateInput) return;
    if (!selectedDates.includes(manualDateInput)) {
      setSelectedDates((prev) => [...prev, manualDateInput].sort());
    }
    setManualDateInput('');
  };

  // Create Interview Slots
  const handleCreateSlots = async (e) => {
    if (e) e.preventDefault();

    if (selectedDates.length === 0) {
      showToast('Please select at least one interview date from the calendar', 'error');
      return;
    }

    // Build time slots array
    let timeSlots = [...selectedPresetTimes.map((s) => ({
      start_time: s.start,
      end_time: s.end,
      capacity: Number(slotCapacity) || 1,
    }))];

    if (customStartTime && customEndTime) {
      timeSlots.push({
        start_time: customStartTime,
        end_time: customEndTime,
        capacity: Number(slotCapacity) || 1,
      });
    }

    if (timeSlots.length === 0) {
      showToast('Please select at least one time slot or enter custom start & end time', 'error');
      return;
    }

    try {
      setCreatingSlots(true);
      const res = await trainingCampAPI.createSlots({
        batch_id: selectedBatchId || null,
        dates: selectedDates,
        time_slots: timeSlots,
        notes: slotNotes,
      });

      if (res.data?.success) {
        showToast(res.data.message || 'Interview slots created successfully', 'success');
        setShowCreateSlotModal(false);
        setSelectedDates([]);
        setSelectedPresetTimes([]);
        setCustomStartTime('');
        setCustomEndTime('');
        loadSlots();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create slots', 'error');
    } finally {
      setCreatingSlots(false);
    }
  };

  // Group slots by date for Cal-style view
  const slotsGroupedByDate = slots.reduce((acc, slot) => {
    const d = slot.date;
    if (!acc[d]) acc[d] = [];
    acc[d].push(slot);
    return acc;
  }, {});

  const sortedDates = Object.keys(slotsGroupedByDate).sort();

  // Helper for rendering multi-date calendar
  const calYear = calendarMonth.getFullYear();
  const calMonth = calendarMonth.getMonth();
  const firstDayOfWeek = new Date(calYear, calMonth, 1).getDay(); // 0 is Sunday
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const monthTitle = calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const todayStr = formatDateYMD(new Date());

  return (
    <div className="tc-admin-container">
      {/* Top Header */}
      <div className="tc-admin-header">
        <div className="tc-admin-title-wrap">
          <span className="tc-admin-camp-badge">
            <Layers size={14} /> Camp 35 &bull; Volunteer Onboarding
          </span>
          <h1 className="tc-admin-title">Instructor's Skill Development & Survival Camp</h1>
          <p className="tc-admin-subtitle">
            Manage volunteer candidate submissions, video introductions, and dynamic interview schedules.
          </p>
        </div>

        {/* Top Right Actions */}
        <div className="tc-header-actions">
          <label className="tc-toggle-wrap" title="Toggle to include all historical/archived camp batches">
            <input
              type="checkbox"
              className="tc-toggle-input"
              checked={showAllCamps}
              onChange={(e) => {
                setShowAllCamps(e.target.checked);
                setSelectedBatchId('');
                setCurrentPage(1);
              }}
            />
            <span className="tc-toggle-slider"></span>
            <span className="tc-toggle-label">Show All Camps (Past Included)</span>
          </label>

          {activeTab === 'slots' && (
            <button
              type="button"
              className="tc-btn tc-btn-primary"
              onClick={() => {
                setShowCreateSlotModal(true);
                setSelectedDates([]);
              }}
            >
              <Plus size={18} /> Add Interview Slots
            </button>
          )}
        </div>
      </div>

      {/* Sub-Section Navigation Tabs */}
      <div className="tc-admin-tabs">
        <button
          type="button"
          className={`tc-admin-tab-btn ${activeTab === 'submissions' ? 'active' : ''}`}
          onClick={() => handleTabChange('submissions')}
        >
          <Users size={18} /> Candidate Submissions & Progress
        </button>
        <button
          type="button"
          className={`tc-admin-tab-btn ${activeTab === 'slots' ? 'active' : ''}`}
          onClick={() => handleTabChange('slots')}
        >
          <CalendarDays size={18} /> Interview Schedule Spots
        </button>
      </div>

      {/* =================== SUB SECTION 1: SUBMISSIONS =================== */}
      {activeTab === 'submissions' && (
        <div>
          {/* Summary Metric Cards */}
          <div className="tc-metrics-grid">
            <div className="tc-metric-card">
              <div className="tc-metric-icon-box tc-metric-icon-blue">
                <Users size={24} />
              </div>
              <div className="tc-metric-info">
                <span className="tc-metric-label">Total Camp Registrations</span>
                <span className="tc-metric-value">{counts.total_registered || 0}</span>
              </div>
            </div>

            <div className="tc-metric-card">
              <div className="tc-metric-icon-box tc-metric-icon-emerald">
                <CheckCircle2 size={24} />
              </div>
              <div className="tc-metric-info">
                <span className="tc-metric-label">Step 1 Completed</span>
                <span className="tc-metric-value">{counts.step1_submitted || 0}</span>
              </div>
            </div>

            <div className="tc-metric-card">
              <div className="tc-metric-icon-box tc-metric-icon-purple">
                <Calendar size={24} />
              </div>
              <div className="tc-metric-info">
                <span className="tc-metric-label">Interview Slots Booked</span>
                <span className="tc-metric-value">{counts.interview_booked || 0}</span>
              </div>
            </div>

            <div className="tc-metric-card">
              <div className="tc-metric-icon-box tc-metric-icon-amber">
                <Clock size={24} />
              </div>
              <div className="tc-metric-info">
                <span className="tc-metric-label">Pending Onboarding</span>
                <span className="tc-metric-value">{counts.drafts_pending || 0}</span>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="tc-filter-card">
            <div className="tc-filter-group">
              {/* Batch Date Filter */}
              <select
                className="tc-filter-select"
                value={selectedBatchId}
                onChange={(e) => {
                  setSelectedBatchId(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">
                  {showAllCamps ? 'All Camp Batches (Past & Upcoming)' : 'All Upcoming Camp Batches'}
                </option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.date_label || b.label}
                  </option>
                ))}
              </select>

              {/* Step Progress Filter */}
              <select
                className="tc-filter-select"
                value={stepFilter}
                onChange={(e) => {
                  setStepFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Progress Steps</option>
                <option value="step1_submitted">Step 1 Completed</option>
                <option value="step1_pending">Step 1 Pending / Not Started</option>
                <option value="interview_booked">Step 2 (Interview) Booked</option>
                <option value="step2_pending">Step 2 (Interview) Pending</option>
                <option value="both_completed">Both Steps Completed</option>
                <option value="draft">Draft Saved Only</option>
              </select>

              {/* Application Review Status Filter */}
              <select
                className="tc-filter-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Review Status</option>
                <option value="pending">Review Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="interview_scheduled">Interview Scheduled</option>
              </select>

              {/* Search Bar */}
              <input
                type="text"
                className="tc-filter-input"
                placeholder="Search by name, phone, or reg #..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setCurrentPage(1);
                    loadSubmissions(1, pageSize);
                  }
                }}
              />
            </div>

            <div className="tc-filter-actions" style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                className="tc-btn tc-btn-primary"
                onClick={handleDownloadCsv}
                title="Download CSV spreadsheet of candidate participants"
              >
                <Download size={16} /> Download CSV
              </button>
              <button
                type="button"
                className="tc-btn tc-btn-secondary"
                onClick={() => loadSubmissions(currentPage, pageSize)}
                title="Refresh table data"
              >
                <RefreshCw size={16} /> Refresh
              </button>
            </div>
          </div>

          {/* Submissions Table */}
          <div className="tc-table-card">
            <div className="tc-table-responsive">
              <table className="tc-table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Gender &amp; DOB</th>
                    <th>Camp Batch Date</th>
                    <th>Step 1 (Basic &amp; Video)</th>
                    <th>Video Intro Link</th>
                    <th>Why Join Link</th>
                    <th>Step 2 (Interview Slot)</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissionsLoading ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                        Loading registered candidates...
                      </td>
                    </tr>
                  ) : submissions.length === 0 ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                        No registered candidates found matching the criteria.
                      </td>
                    </tr>
                  ) : (
                    submissions.map((sub) => {
                      const age = sub.dob
                        ? Math.floor((new Date() - new Date(sub.dob)) / (365.25 * 24 * 60 * 60 * 1000))
                        : null;

                      const isStep1Done = Boolean(sub.is_step1_submitted || sub.isStep1Submitted);
                      const isInterviewDone = Boolean(sub.is_interview_booked || sub.isInterviewBooked);
                      const videoIntro = sub.video_intro_url || sub.videoIntroUrl;
                      const whyJoin = sub.why_join_url || sub.whyJoinUrl;
                      const bloodGrp = sub.bloodGroup || sub.blood_group;
                      const activeSlot = sub.slot || sub.interviewSlot;

                      return (
                        <tr key={sub.id || sub.registrationId}>
                          <td>
                            <div className="tc-user-cell">
                              <span className="tc-user-name">{sub.name || 'Candidate'}</span>
                              <span className="tc-user-phone">{sub.phone || '—'}</span>
                              {sub.registrationId && (
                                <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                  Reg #{sub.registrationId}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div>
                              <span>{sub.gender || '—'}</span>
                              {age ? <span style={{ color: '#64748b', fontSize: '0.8rem' }}> ({age} yrs)</span> : null}
                              {bloodGrp ? (
                                <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 4, background: '#fee2e2', color: '#b91c1c', fontSize: '0.75rem', fontWeight: 700 }}>
                                  {bloodGrp}
                                </span>
                              ) : null}
                            </div>
                            {sub.dob && (
                              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                {new Date(sub.dob).toLocaleDateString('en-IN')}
                              </span>
                            )}
                          </td>
                          <td>
                            <span style={{ fontWeight: 600 }}>
                              {sub.batch_date_label || sub.batchDateLabel || '—'}
                            </span>
                          </td>
                          <td>
                            {isStep1Done ? (
                              <div>
                                <span className="tc-badge-step tc-badge-step1">
                                  <CheckCircle2 size={12} /> Step 1 Complete
                                </span>
                                {sub.step1_submitted_at && (
                                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                                    {new Date(sub.step1_submitted_at).toLocaleDateString('en-IN')}
                                  </div>
                                )}
                              </div>
                            ) : sub.status === 'draft' ? (
                              <span className="tc-badge-step tc-badge-draft">
                                <Clock size={12} /> Draft Saved
                              </span>
                            ) : (
                              <span className="tc-badge-step tc-badge-notstarted">
                                <AlertCircle size={12} /> Not Started
                              </span>
                            )}
                          </td>
                          <td>
                            {videoIntro ? (
                              <a
                                href={videoIntro}
                                target="_blank"
                                rel="noreferrer"
                                className="tc-link-chip tc-link-chip-video"
                                title="Open Video Introduction in Google Drive"
                              >
                                <Video size={14} /> View Video <ExternalLink size={12} />
                              </a>
                            ) : (
                              <span className="tc-link-empty">—</span>
                            )}
                          </td>
                          <td>
                            {whyJoin ? (
                              <a
                                href={whyJoin}
                                target="_blank"
                                rel="noreferrer"
                                className="tc-link-chip tc-link-chip-why"
                                title="Open Why Join Invincible Video in Google Drive"
                              >
                                <FileText size={14} /> Why Join <ExternalLink size={12} />
                              </a>
                            ) : (
                              <span className="tc-link-empty">—</span>
                            )}
                          </td>
                          <td>
                            {isInterviewDone && activeSlot ? (
                              <div>
                                <span className="tc-badge-step tc-badge-interview">
                                  <CheckCircle2 size={12} /> Booked
                                </span>
                                <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.85rem', marginTop: '3px' }}>
                                  {activeSlot.date}
                                </div>
                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                  {activeSlot.start_time || activeSlot.startTime} - {activeSlot.end_time || activeSlot.endTime}
                                </span>
                              </div>
                            ) : isStep1Done ? (
                              <span className="tc-badge-step tc-badge-pending-slot">
                                <Clock size={12} /> Slot Pending
                              </span>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                                Pending Step 1
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              className="tc-btn-icon"
                              title="Review Candidate Application"
                              onClick={() => {
                                setReviewCandidate(sub);
                                setUpdateStatus(sub.status || 'pending');
                                setAdminNotes(sub.admin_notes || sub.adminNotes || '');
                              }}
                            >
                              <Edit size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="tc-pagination-bar">
              <div className="tc-pagination-info">
                <span>
                  Showing{' '}
                  <strong>
                    {pagination.total === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                  </strong>{' '}
                  -{' '}
                  <strong>
                    {Math.min(currentPage * pageSize, pagination.total || submissions.length)}
                  </strong>{' '}
                  of <strong>{pagination.total || submissions.length}</strong> candidates
                </span>
                <div className="tc-pagination-limit-wrap">
                  <span>Rows:</span>
                  <select
                    className="tc-pagination-limit-select"
                    value={pageSize}
                    onChange={(e) => {
                      const newSize = Number(e.target.value);
                      setPageSize(newSize);
                      setCurrentPage(1);
                      loadSubmissions(1, newSize);
                    }}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </div>
              </div>

              <div className="tc-pagination-controls">
                <button
                  type="button"
                  className="tc-page-btn"
                  disabled={currentPage <= 1 || submissionsLoading}
                  onClick={() => {
                    const p = currentPage - 1;
                    setCurrentPage(p);
                    loadSubmissions(p, pageSize);
                  }}
                  title="Previous Page"
                >
                  <ChevronLeft size={16} /> Prev
                </button>

                {Array.from({ length: Math.min(5, pagination.totalPages || 1) }, (_, i) => {
                  let pageNum = i + 1;
                  if (pagination.totalPages > 5 && currentPage > 3) {
                    pageNum = currentPage - 2 + i;
                    if (pageNum > pagination.totalPages) pageNum = pagination.totalPages - (4 - i);
                  }
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`tc-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                      onClick={() => {
                        setCurrentPage(pageNum);
                        loadSubmissions(pageNum, pageSize);
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  type="button"
                  className="tc-page-btn"
                  disabled={currentPage >= (pagination.totalPages || 1) || submissionsLoading}
                  onClick={() => {
                    const p = currentPage + 1;
                    setCurrentPage(p);
                    loadSubmissions(p, pageSize);
                  }}
                  title="Next Page"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =================== SUB SECTION 2: INTERVIEW SLOTS =================== */}
      {activeTab === 'slots' && (
        <div>
          {/* Slots Top Filter */}
          <div className="tc-filter-card" style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
              <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>
                Filter Slots by Batch Date:
              </label>
              <select
                className="tc-filter-select"
                value={selectedBatchId}
                onChange={(e) => setSelectedBatchId(e.target.value)}
              >
                <option value="">
                  {showAllCamps ? 'All Training Camp Dates (Past & Upcoming)' : 'All Upcoming Camp Dates'}
                </option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.date_label || b.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className="tc-btn tc-btn-secondary"
                onClick={loadSlots}
              >
                <RefreshCw size={16} /> Refresh Slots
              </button>
              <button
                type="button"
                className="tc-btn tc-btn-primary"
                onClick={() => {
                  setShowCreateSlotModal(true);
                  setSelectedDates([]);
                }}
              >
                <Plus size={18} /> Add Slots
              </button>
            </div>
          </div>

          {/* Slots List Grouped by Date */}
          {slotsLoading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              Loading interview slots...
            </div>
          ) : sortedDates.length === 0 ? (
            <div className="tc-table-card" style={{ padding: '60px', textAlign: 'center' }}>
              <Calendar size={48} style={{ color: '#cbd5e1', marginBottom: '16px' }} />
              <h3 style={{ margin: 0, color: '#0f172a' }}>No Interview Slots Configured</h3>
              <p style={{ color: '#64748b', maxWidth: '420px', margin: '8px auto 20px' }}>
                Create dynamic interview schedule spots for your volunteer candidates to book.
              </p>
              <button
                type="button"
                className="tc-btn tc-btn-primary"
                onClick={() => {
                  setShowCreateSlotModal(true);
                  setSelectedDates([]);
                }}
              >
                <Plus size={18} /> Create First Batch of Slots
              </button>
            </div>
          ) : (
            sortedDates.map((dateStr) => {
              const daySlots = slotsGroupedByDate[dateStr];
              const totalCap = daySlots.reduce((sum, s) => sum + (s.capacity || 1), 0);
              const totalBooked = daySlots.reduce((sum, s) => sum + (s.booked_count || 0), 0);

              const formattedDate = new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              });

              return (
                <div key={dateStr} className="tc-date-section">
                  <div className="tc-date-section-header">
                    <h3 className="tc-date-heading">
                      <Calendar size={20} style={{ color: '#de5824' }} /> {formattedDate}
                    </h3>
                    <div className="tc-date-stats">
                      <strong>{daySlots.length}</strong> slots &bull;{' '}
                      <strong>{totalBooked}</strong> / {totalCap} Booked
                    </div>
                  </div>

                  <div className="tc-slots-cards-grid">
                    {daySlots.map((slot) => {
                      const slotVisible = slot.isVisible !== undefined ? slot.isVisible : (slot.is_visible !== undefined ? Boolean(slot.is_visible) : true);
                      const startTime = slot.startTime || slot.start_time || '';
                      const endTime = slot.endTime || slot.end_time || '';
                      const bookedCount = slot.bookedCount ?? slot.booked_count ?? 0;
                      const capacity = slot.capacity || 1;
                      const isFull = bookedCount >= capacity;
                      const fillPercent = Math.min(
                        100,
                        Math.round((bookedCount / capacity) * 100)
                      );

                      return (
                        <div
                          key={slot.id}
                          className={`tc-admin-slot-card ${!slotVisible ? 'hidden-slot' : ''}`}
                        >
                          <div className="tc-admin-slot-top">
                            <div className="tc-admin-slot-time">
                              <Clock size={16} />
                              <span>{startTime} - {endTime}</span>
                            </div>

                            <span
                              style={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: slotVisible ? '#ecfdf5' : '#f1f5f9',
                                color: slotVisible ? '#059669' : '#64748b',
                              }}
                            >
                              {slotVisible ? 'Visible' : 'Hidden'}
                            </span>
                          </div>

                          {/* Capacity Bar */}
                          <div className="tc-capacity-bar-wrap">
                            <div className="tc-capacity-info">
                              <span>Capacity</span>
                              <span>
                                {bookedCount} / {capacity} booked
                              </span>
                            </div>
                            <div className="tc-capacity-bar">
                              <div
                                className={`tc-capacity-fill ${
                                  isFull ? 'full' : fillPercent > 60 ? 'high' : ''
                                }`}
                                style={{ width: `${fillPercent}%` }}
                              ></div>
                            </div>
                          </div>

                          {/* Booked Candidates Names */}
                          {slot.candidates && slot.candidates.length > 0 && (
                            <div className="tc-slot-booked-names">
                              {slot.candidates.map((c) => (
                                <div key={c.id} className="tc-booked-person-pill">
                                  <Users size={12} />
                                  <span>{c.name} ({c.phone})</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {slot.notes && (
                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b' }}>
                              &ldquo;{slot.notes}&rdquo;
                            </p>
                          )}

                          <div className="tc-admin-slot-actions">
                            <button
                              type="button"
                              className="tc-btn-icon"
                              title={slotVisible ? 'Hide from candidates' : 'Show to candidates'}
                              onClick={() => handleToggleSlotVisibility(slot)}
                            >
                              {slotVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>

                            <button
                              type="button"
                              className="tc-btn-icon danger"
                              title="Delete Slot"
                              onClick={() => handleDeleteSlot(slot.id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ================= REVIEW CANDIDATE MODAL ================= */}
      {reviewCandidate && (
        <div className="tc-modal-overlay">
          <div className="tc-modal-card">
            <div className="tc-modal-header">
              <h3>Review Candidate Application</h3>
              <button
                type="button"
                className="tc-btn-icon"
                onClick={() => setReviewCandidate(null)}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 4px', color: '#0f172a' }}>{reviewCandidate.name}</h4>
              <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
                Phone: {reviewCandidate.phone} | Gender: {reviewCandidate.gender || '—'} | DOB: {reviewCandidate.dob ? reviewCandidate.dob.split('T')[0] : '—'} | Blood Group: <strong style={{ color: '#b91c1c' }}>{reviewCandidate.bloodGroup || reviewCandidate.blood_group || '—'}</strong>
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              {reviewCandidate.video_intro_url && (
                <a
                  href={reviewCandidate.video_intro_url}
                  target="_blank"
                  rel="noreferrer"
                  className="tc-link-chip"
                >
                  <Video size={16} /> View Intro Video
                </a>
              )}
              {reviewCandidate.why_join_url && (
                <a
                  href={reviewCandidate.why_join_url}
                  target="_blank"
                  rel="noreferrer"
                  className="tc-link-chip"
                >
                  <FileText size={16} /> View 'Why Join' Video
                </a>
              )}
            </div>

            <div className="tc-form-row">
              <label>Application Status:</label>
              <select
                value={updateStatus}
                onChange={(e) => setUpdateStatus(e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved (Set as Volunteer in Registrations)</option>
                <option value="rejected">Rejected (Set as Cancellation Request in Registrations)</option>
                <option value="interview_scheduled">Interview Scheduled</option>
              </select>
              {updateStatus === 'approved' && (
                <span style={{ fontSize: '0.775rem', color: '#16a34a', fontWeight: 600, marginTop: '2px' }}>
                  ✓ Approving will automatically change the candidate&apos;s status to &ldquo;volunteer&rdquo; in the main registration record.
                </span>
              )}
              {updateStatus === 'rejected' && (
                <span style={{ fontSize: '0.775rem', color: '#dc2626', fontWeight: 600, marginTop: '2px' }}>
                  ✕ Rejecting will automatically set the candidate&apos;s status to &ldquo;cancellation request&rdquo; in the main registration record.
                </span>
              )}
            </div>

            <div className="tc-form-row">
              <label>Admin Internal Notes / Feedback:</label>
              <textarea
                rows="4"
                placeholder="Add private evaluation notes, interview impressions, etc..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
              ></textarea>
            </div>

            <div className="tc-modal-footer">
              <button
                type="button"
                className="tc-btn tc-btn-secondary"
                onClick={() => setReviewCandidate(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tc-btn tc-btn-primary"
                disabled={savingNotes}
                onClick={handleSaveReview}
              >
                {savingNotes ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= CREATE SLOTS MODAL WITH MULTI-DATE CALENDAR ================= */}
      {showCreateSlotModal && (
        <div className="tc-modal-overlay">
          <div className="tc-modal-card" style={{ maxWidth: '680px' }}>
            <div className="tc-modal-header">
              <h3>Create Interview Schedule Spots</h3>
              <button
                type="button"
                className="tc-btn-icon"
                onClick={() => setShowCreateSlotModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateSlots}>
              {/* Batch Association */}
              <div className="tc-form-row">
                <label>Training Camp Batch Date (Optional Association):</label>
                <select
                  value={selectedBatchId}
                  onChange={(e) => setSelectedBatchId(e.target.value)}
                >
                  <option value="">Applies to All / General</option>
                  {batches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.date_label || b.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Interactive Multi-Date Calendar Picker */}
              <div className="tc-form-row">
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    Select Interview Dates directly from Calendar <span style={{ color: '#ef4444' }}>*</span>
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Click multiple dates to select/unselect
                  </span>
                </label>

                <div className="tc-calendar-widget">
                  <div className="tc-calendar-header">
                    <span className="tc-calendar-month-title">{monthTitle}</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        className="tc-calendar-nav-btn"
                        title="Previous Month"
                        onClick={() => {
                          setCalendarMonth(new Date(calYear, calMonth - 1, 1));
                        }}
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        type="button"
                        className="tc-calendar-nav-btn"
                        style={{ width: 'auto', padding: '0 8px', fontSize: '0.75rem', fontWeight: 600 }}
                        onClick={() => setCalendarMonth(new Date())}
                      >
                        Today
                      </button>
                      <button
                        type="button"
                        className="tc-calendar-nav-btn"
                        title="Next Month"
                        onClick={() => {
                          setCalendarMonth(new Date(calYear, calMonth + 1, 1));
                        }}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="tc-calendar-weekdays">
                    {WEEKDAYS.map((w) => (
                      <div key={w}>{w}</div>
                    ))}
                  </div>

                  <div className="tc-calendar-days-grid">
                    {/* Blank cells before 1st of month */}
                    {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                      <div key={`empty-${i}`} className="tc-calendar-day-cell empty" />
                    ))}

                    {/* Month days */}
                    {Array.from({ length: daysInMonth }).map((_, i) => {
                      const dayNum = i + 1;
                      const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                      const isSelected = selectedDates.includes(dateStr);
                      const isToday = dateStr === todayStr;

                      return (
                        <button
                          key={dateStr}
                          type="button"
                          className={`tc-calendar-day-cell ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                          onClick={() => toggleDateSelection(dateStr)}
                        >
                          {dayNum}
                        </button>
                      );
                    })}
                  </div>

                  {/* Selected Dates Tray */}
                  <div className="tc-selected-dates-tray">
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155' }}>
                        Selected Dates ({selectedDates.length}):
                      </span>
                      {selectedDates.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedDates([])}
                          style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    {selectedDates.length === 0 ? (
                      <span style={{ fontSize: '0.775rem', color: '#94a3b8', fontStyle: 'italic' }}>
                        Click on dates in the calendar above to select them.
                      </span>
                    ) : (
                      selectedDates.map((d) => (
                        <span key={d} className="tc-date-pill">
                          <Calendar size={12} /> {formatDatePill(d)}
                          <button
                            type="button"
                            className="tc-date-pill-remove"
                            onClick={() => toggleDateSelection(d)}
                            title="Remove date"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                </div>

                {/* Direct date picker fallback */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                  <span style={{ fontSize: '0.775rem', color: '#64748b' }}>Or pick date manually:</span>
                  <input
                    type="date"
                    style={{ height: '32px', padding: '0 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                    value={manualDateInput}
                    onChange={(e) => setManualDateInput(e.target.value)}
                  />
                  <button
                    type="button"
                    className="tc-btn tc-btn-secondary"
                    style={{ padding: '4px 10px', fontSize: '0.75rem', height: '32px' }}
                    onClick={handleAddManualDate}
                  >
                    Add Date
                  </button>
                </div>
              </div>

              {/* Range Selector with 30-minute intervals (Multiple Add) */}
              <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <Clock size={16} style={{ color: '#de5824' }} />
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>
                    Generate 30-Minute Time Slots in Range:
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px', alignItems: 'flex-end' }}>
                  <div className="tc-form-row" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.775rem', fontWeight: 600 }}>Start Time:</label>
                    <select
                      className="tc-form-select"
                      value={rangeStartTime}
                      onChange={(e) => setRangeStartTime(e.target.value)}
                    >
                      {TIME_SELECT_OPTIONS.map((t) => (
                        <option key={`start-${t}`} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="tc-form-row" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.775rem', fontWeight: 600 }}>End Time:</label>
                    <select
                      className="tc-form-select"
                      value={rangeEndTime}
                      onChange={(e) => setRangeEndTime(e.target.value)}
                    >
                      {TIME_SELECT_OPTIONS.map((t) => (
                        <option key={`end-${t}`} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    className="tc-btn tc-btn-primary"
                    style={{ height: '38px', padding: '0 16px', fontSize: '0.8rem', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    onClick={handleAddRangeSlots}
                  >
                    <Plus size={16} /> Add 30-min Slots
                  </button>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '6px' }}>
                  Select start and end time to automatically create 30-minute interval interview spots (e.g. 10:00 AM to 01:00 PM creates 6 slots). Multiple additions supported.
                </div>
              </div>

              {/* Selected Time Slots Display (Chips with removal) */}
              <div className="tc-form-row">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ margin: 0, fontWeight: 700 }}>
                    Selected Time Slots ({selectedPresetTimes.length}):
                  </label>
                  {selectedPresetTimes.length > 0 && (
                    <button
                      type="button"
                      onClick={clearAllSelectedTimeSlots}
                      style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {selectedPresetTimes.length === 0 ? (
                  <div style={{ padding: '12px', border: '1px dashed #cbd5e1', borderRadius: '8px', color: '#94a3b8', fontSize: '0.8rem', textAlign: 'center' }}>
                    No time slots added yet. Use the Start/End time fields above or toggle individual slots below.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '160px', overflowY: 'auto', padding: '10px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
                    {selectedPresetTimes.map((s) => (
                      <span
                        key={`${s.start}-${s.end}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          background: '#fff7ed',
                          color: '#c2410c',
                          border: '1px solid #fed7aa',
                          fontSize: '0.8rem',
                          fontWeight: 600
                        }}
                      >
                        <Clock size={12} />
                        {s.start} - {s.end}
                        <button
                          type="button"
                          onClick={() => removeSelectedTimeSlot(s)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#ea580c',
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: 0
                          }}
                          title="Remove slot"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Preset Slots Accordion / Buttons */}
              <div className="tc-form-row">
                <label style={{ fontSize: '0.8rem', color: '#64748b' }}>Or toggle individual preset 30-min slots:</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '120px', overflowY: 'auto', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  {PRESET_TIME_SLOTS.map((s) => {
                    const isSelected = selectedPresetTimes.some(
                      (item) => item.start === s.start && item.end === s.end
                    );
                    return (
                      <button
                        key={`${s.start}-${s.end}`}
                        type="button"
                        onClick={() => togglePresetTime(s)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          border: isSelected ? '1px solid #de5824' : '1px solid #cbd5e1',
                          backgroundColor: isSelected ? '#fff7ed' : '#ffffff',
                          color: isSelected ? '#c2410c' : '#334155',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {s.start} - {s.end}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Optional Custom Single Slot */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="tc-form-row">
                  <label>Or One-off Custom Start Time:</label>
                  <input
                    type="text"
                    placeholder="e.g. 08:00 PM"
                    value={customStartTime}
                    onChange={(e) => setCustomStartTime(e.target.value)}
                  />
                </div>
                <div className="tc-form-row">
                  <label>One-off Custom End Time:</label>
                  <input
                    type="text"
                    placeholder="e.g. 08:30 PM"
                    value={customEndTime}
                    onChange={(e) => setCustomEndTime(e.target.value)}
                  />
                </div>
              </div>

              {/* Spots Capacity per Slot */}
              <div className="tc-form-row">
                <label>
                  Spots per Time Slot (Capacity):
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={slotCapacity}
                  onChange={(e) => setSlotCapacity(e.target.value)}
                  required
                />
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  How many candidates can book this exact slot simultaneously (e.g. 1 for 1-on-1 interview).
                </span>
              </div>

              {/* Notes */}
              <div className="tc-form-row">
                <label>Interview Instructions / Google Meet Link:</label>
                <input
                  type="text"
                  placeholder="e.g. Google Meet link or call instructions"
                  value={slotNotes}
                  onChange={(e) => setSlotNotes(e.target.value)}
                />
              </div>

              <div className="tc-modal-footer">
                <button
                  type="button"
                  className="tc-btn tc-btn-secondary"
                  onClick={() => setShowCreateSlotModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingSlots}
                  className="tc-btn tc-btn-primary"
                >
                  {creatingSlots ? 'Generating Slots...' : `Generate Slots for ${selectedDates.length || 0} Date(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingCamp;
